import React, { useEffect, useState, useRef } from 'react';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { FancyButton } from '../../common/buttons';
import Cropper from 'react-easy-crop';
import { storeFile } from '../../../firebase/CloudStorage';

const editorStyle = {
    display:'flex',
    flexDirection:'column',
    alignItems:'center',
    justifyContent:'center',
    gap:'24px',
    minHeight:'520px',
    boxSizing:'border-box',
}

const cropperShellStyle = {
    position:'relative',
    width:'320px',
    height:'320px',
    borderRadius:'50%',
    overflow:'hidden',
    backgroundColor:'#f3f4f6',
    boxShadow:'0 14px 40px rgba(0, 0, 0, 0.18)',
}

const emptyAvatarStyle = {
    ...cropperShellStyle,
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    border:'2px dashed rgba(0, 0, 0, 0.18)',
    boxShadow:'none',
    color:'#6b7280',
    fontWeight:600,
}

const createImage = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
});

const getCroppedAvatarBlob = async (imageSrc, croppedAreaPixels) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const size = Math.min(croppedAreaPixels.width, croppedAreaPixels.height);
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    context.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        size,
        size,
        0,
        0,
        size,
        size
    );

    return await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png');
    });
}

const storeFileAsync = (filename, file) => new Promise((resolve, reject) => {
    storeFile(filename, file, resolve, null, reject);
});

export function UploadAvatar(props) {
    const fileInputRef = useRef();
    const savedProfilePicUrl = props.userAccount.profilePicUrl ? props.userAccount.profilePicUrl : '';
    const avatarOptions = props.userAccount && props.userAccount.avatarOptions ? props.userAccount.avatarOptions : null;
    const initialCrop = avatarOptions && avatarOptions.crop ? avatarOptions.crop : { x: 0, y: 0 };
    const initialZoom = avatarOptions && avatarOptions.zoom ? avatarOptions.zoom : 1;
    const initialCroppedArea = avatarOptions && avatarOptions.croppedArea ? avatarOptions.croppedArea : null;
    const initialCroppedAreaPixels = avatarOptions && avatarOptions.croppedAreaPixels ? avatarOptions.croppedAreaPixels : null;
    const [crop, setCrop] = useState(initialCrop);
    const [croppedArea, setCroppedArea] = useState(initialCroppedArea);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(initialCroppedAreaPixels);
    const [zoom, setZoom] = useState(initialZoom);
    const [saving, setSaving] = useState(false);
    const [selectedProfilePicFile, setSelectedProfilePicFile] = useState(null);
    const [selectedProfilePicPreviewUrl, setSelectedProfilePicPreviewUrl] = useState('');
    const profilePicUrl = selectedProfilePicPreviewUrl || savedProfilePicUrl;

    useEffect(() => {
        return () => {
            if(selectedProfilePicPreviewUrl) URL.revokeObjectURL(selectedProfilePicPreviewUrl);
        };
    }, [selectedProfilePicPreviewUrl]);

    const onSelectProfilePic = (event) =>{
        const file = event.currentTarget.files[0];
        event.currentTarget.value = '';
        if(!file) return;

        if(selectedProfilePicPreviewUrl) URL.revokeObjectURL(selectedProfilePicPreviewUrl);
        setSelectedProfilePicFile(file);
        setSelectedProfilePicPreviewUrl(URL.createObjectURL(file));
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedArea(null);
        setCroppedAreaPixels(null);
    }

    const saveAvatar = async () => {
        if(!profilePicUrl || !croppedAreaPixels) return;

        let nextProfilePicUrl = savedProfilePicUrl;
        if(selectedProfilePicFile){
            nextProfilePicUrl = await storeFileAsync(`${props.userAccount.id}_ProfilePic`, selectedProfilePicFile);
        }

        const blob = await getCroppedAvatarBlob(profilePicUrl, croppedAreaPixels);
        const filename = props.userAccount.id + "_Avatar";
        const updatedUserAccount = {
            ...props.userAccount,
            profilePicUrl: nextProfilePicUrl,
            avatarOptions: {
                crop,
                croppedArea,
                croppedAreaPixels,
                zoom
            }
        };

        const avatarUrl = await storeFileAsync(filename, blob);
        const accountWithAvatar = {...updatedUserAccount, avatarUrl};
        accountWithAvatar.avatarVersion = new Date().getTime();
        await props.storeUserAccount(updatedUserAccount.id, accountWithAvatar);
        props.setUserAccount(accountWithAvatar);
        return accountWithAvatar;
    }

    const onSave = async () => {
        setSaving(true);
        try {
            const updatedUserAccount = await saveAvatar();
            props.onDone(updatedUserAccount);
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <DialogContent style={{flex:1}}>
                <div style={editorStyle}>
                    {profilePicUrl &&
                        <div style={cropperShellStyle}>
                            <Cropper
                                image={profilePicUrl}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onCropComplete={(nextCroppedArea, nextCroppedAreaPixels) => {
                                    setCroppedArea(nextCroppedArea);
                                    setCroppedAreaPixels(nextCroppedAreaPixels);
                                }}
                                onZoomChange={setZoom}
                            />
                        </div>
                    }
                    {!profilePicUrl &&
                        <div style={emptyAvatarStyle}>No photo selected</div>
                    }
                    <FancyButton onClick={()=>fileInputRef.current.click()}>
                        Upload Photo
                    </FancyButton>
                </div>
            </DialogContent>
            <DialogActions style={{display:'flex', flexDirection:'row', justifyContent:'space-between', padding:'20px', paddingLeft:'24px', paddingRight:'24px'}}>
                <FancyButton disabled={saving} onClick={props.onCancel || props.onDone}>Cancel</FancyButton>
                <FancyButton variant="dark" loading={saving} disabled={!profilePicUrl || !croppedAreaPixels} onClick={onSave}>Save</FancyButton>
                <input accept="image/*" onChange={onSelectProfilePic} multiple={false} ref={fileInputRef} type="file" hidden/>
            </DialogActions>
        </>
    );
}
